import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Chip,
  Grid,
  Divider,
  Icon,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import { rfqService } from "../../services/rfq.service";
import { supplierService } from "../../services/supplier.service";
import {
  RFQ,
  VendorQuotation,
  VendorQuotationItem,
  VendorQuotationStatus,
  AddVendorQuotationRequest,
  UpdateVendorQuotationRequest,
} from "../../types/rfq.types";
import { VendorQuotationComparison } from "./VendorQuotationComparison";
import { EnhancedVendorQuotationImport } from "./EnhancedVendorQuotationImport";
import { hasPermission } from "../../../auth/utils/permission.utils";
import { useAuth } from "../../../auth/hooks/useAuth";
interface VendorQuotationsDialogProps {
  open: boolean;
  onClose: () => void;
  rfq: RFQ;
  onUpdate: () => void;
}

interface QuotationFormData {
  supplier_id: string;
  supplier_name: string;
  quoted_price: number;
  quoted_price_override: boolean;
  calculated_price: number; // Sum of all item totals
  estimated_delivery_days: number;
  quote_validity_date: Date | null;
  currency: string;
  payment_terms: string;
  delivery_terms: string;
  notes: string;
  items: VendorQuotationItem[];
}

export const VendorQuotationsDialog: React.FC<VendorQuotationsDialogProps> = ({
  open,
  onClose,
  rfq,
  onUpdate,
}) => {
  const { user } = useAuth();
  const [quotations, setQuotations] = useState<VendorQuotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] =
    useState<VendorQuotation | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(
    null
  );
  const [activeTab, setActiveTab] = useState(0);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [quotationToReject, setQuotationToReject] = useState<string | null>(
    null
  );
  const [rejectionReason, setRejectionReason] = useState("");

  const [formData, setFormData] = useState<QuotationFormData>({
    supplier_id: "",
    supplier_name: "",
    quoted_price: 0,
    quoted_price_override: false,
    calculated_price: 0,
    estimated_delivery_days: 7,
    quote_validity_date: null,
    currency: "USD",
    payment_terms: "",
    delivery_terms: "",
    notes: "",
    items: [],
  });

  useEffect(() => {
    if (open) {
      loadQuotations();
    }
  }, [open, rfq._id]);

  // Calculate total when items change
  useEffect(() => {
    const calculatedPrice = calculateTotalPrice(formData.items);
    if (Math.abs(calculatedPrice - formData.calculated_price) > 0.01) {
      setFormData((prev) => ({
        ...prev,
        calculated_price: calculatedPrice,
        // Update quoted_price if not overridden
        quoted_price: prev.quoted_price_override
          ? prev.quoted_price
          : calculatedPrice,
      }));
    }
  }, [formData.items]);

  const loadQuotations = async () => {
    setLoading(true);
    try {
      const response = await rfqService.getRFQQuotations({ _id: rfq._id });
      if (response.success && response.data) {
        setQuotations(response.data);
      } else {
        throw new Error(response.error?.message || "Failed to load quotations");
      }
    } catch (error: any) {
      console.error("Error loading quotations:", error);
      toast.error(error.message || "Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: VendorQuotationStatus) => {
    const config = {
      pending: { label: "Pending", color: "#64748B", bgColor: "#F1F5F9" },
      submitted: { label: "Submitted", color: "#3B82F6", bgColor: "#EFF6FF" },
      accepted: { label: "Accepted", color: "#059669", bgColor: "#ECFDF5" },
      rejected: { label: "Rejected", color: "#DC2626", bgColor: "#FEF2F2" },
    };

    const statusConfig = config[status];
    return (
      <Chip
        label={statusConfig.label}
        sx={{
          bgcolor: statusConfig.bgColor,
          color: statusConfig.color,
          fontWeight: 600,
          fontSize: 12,
          height: 28,
        }}
      />
    );
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const isQuotationExpired = (validityTimestamp: number) => {
    return validityTimestamp < Date.now();
  };

  const calculateTotalPrice = (items: VendorQuotationItem[]) => {
    return items.reduce((sum, item) => sum + item.total_price, 0);
  };

  const handleItemPriceChange = (index: number, pricePerUnit: number) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      price_per_unit: pricePerUnit,
      total_price: pricePerUnit * newItems[index].quantity,
    };

    const newCalculatedPrice = calculateTotalPrice(newItems);

    setFormData((prev) => ({
      ...prev,
      items: newItems,
      calculated_price: newCalculatedPrice,
      // Update quoted_price if not overridden
      quoted_price: prev.quoted_price_override
        ? prev.quoted_price
        : newCalculatedPrice,
    }));
  };

  const handleItemTotalChange = (index: number, totalPrice: number) => {
    const newItems = [...formData.items];
    const quantity = newItems[index].quantity;

    newItems[index] = {
      ...newItems[index],
      total_price: totalPrice,
      // Recalculate price_per_unit based on new total
      price_per_unit: quantity > 0 ? totalPrice / quantity : 0,
    };

    const newCalculatedPrice = calculateTotalPrice(newItems);

    setFormData((prev) => ({
      ...prev,
      items: newItems,
      calculated_price: newCalculatedPrice,
      // Update quoted_price if not overridden
      quoted_price: prev.quoted_price_override
        ? prev.quoted_price
        : newCalculatedPrice,
    }));
  };

  const handleQuotedPriceChange = (value: number, isManualEdit = false) => {
    setFormData((prev) => ({
      ...prev,
      quoted_price: value,
      quoted_price_override: isManualEdit || prev.quoted_price_override,
    }));
  };

  const resetToCalculatedPrice = () => {
    setFormData((prev) => ({
      ...prev,
      quoted_price: prev.calculated_price,
      quoted_price_override: false,
    }));
  };

  const getQuotationWithScores = (quotation: VendorQuotation) => {
    // Calculate delivery score (1-10, 10 being fastest)
    const maxDeliveryDays = Math.max(
      ...quotations.map((q) => q.estimated_delivery_days),
      30
    );
    const deliveryScore = Math.round(
      (1 - quotation.estimated_delivery_days / maxDeliveryDays) * 9 + 1
    );

    // Calculate price score (1-10, 10 being cheapest)
    const prices = quotations.map((q) => q.quoted_price).filter((p) => p > 0);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const priceScore =
      priceRange > 0
        ? Math.round(
            (1 - (quotation.quoted_price - minPrice) / priceRange) * 9 + 1
          )
        : 10;

    return {
      ...quotation,
      is_expired: isQuotationExpired(quotation.quote_validity_date),
      delivery_score: deliveryScore,
      price_score: priceScore,
    };
  };

  const handleAddQuotation = () => {
    if (availableSuppliers.length === 0) {
      toast.error("No suppliers available for quotation");
      return;
    }

    // Helper function to convert urgency to lowercase
    const normalizeUrgency = (
      urgency?: string
    ): "low" | "medium" | "high" | undefined => {
      if (!urgency) return undefined;
      const lower = urgency.toLowerCase();
      if (lower === "low" || lower === "medium" || lower === "high") {
        return lower as "low" | "medium" | "high";
      }
      return undefined;
    };

    // Initialize items from RFQ pr_items
    const initialItems: VendorQuotationItem[] = (rfq.pr_items || []).map(
      (item) => ({
        // item_id is not available in pr_items, will be omitted (optional field)
        item_name: item.item_name,
        quantity: item.quantity,
        uom: item.uom,
        price_per_unit: 0,
        total_price: 0,
        urgency: normalizeUrgency(item.urgency),
        reason: item.reason,
      })
    );

    // Start with empty form - let user select supplier manually
    setFormData({
      supplier_id: "",
      supplier_name: "",
      quoted_price: 0,
      quoted_price_override: false,
      calculated_price: 0,
      estimated_delivery_days: 7,
      quote_validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      currency: "USD",
      payment_terms: "",
      delivery_terms: "",
      notes: "",
      items: initialItems,
    });
    setEditingQuotation(null);
    setFormOpen(true);
  };

  const handleEditQuotation = (quotation: VendorQuotation) => {
    // Helper function to convert urgency to lowercase
    const normalizeUrgency = (
      urgency?: string
    ): "low" | "medium" | "high" | undefined => {
      if (!urgency) return undefined;
      const lower = urgency.toLowerCase();
      if (lower === "low" || lower === "medium" || lower === "high") {
        return lower as "low" | "medium" | "high";
      }
      return undefined;
    };

    // Use existing items if available, otherwise initialize from RFQ pr_items
    const quotationItems =
      quotation.items ||
      (rfq.pr_items || []).map((item) => ({
        // item_id is not available in pr_items, will be omitted (optional field)
        item_name: item.item_name,
        quantity: item.quantity,
        uom: item.uom,
        price_per_unit: 0,
        total_price: 0,
        urgency: normalizeUrgency(item.urgency),
        reason: item.reason,
      }));

    const calculatedPrice = quotationItems.reduce(
      (sum, item) => sum + item.total_price,
      0
    );

    setFormData({
      supplier_id: quotation.supplier_id,
      supplier_name: quotation.supplier_name,
      quoted_price: quotation.quoted_price,
      quoted_price_override:
        quotation.quoted_price_override ||
        Math.abs(quotation.quoted_price - calculatedPrice) > 0.01,
      calculated_price: calculatedPrice,
      estimated_delivery_days: quotation.estimated_delivery_days,
      quote_validity_date: new Date(quotation.quote_validity_date),
      currency: quotation.currency || "USD",
      payment_terms: quotation.payment_terms || "",
      delivery_terms: quotation.delivery_terms || "",
      notes: quotation.notes || "",
      items: quotationItems,
    });
    setEditingQuotation(quotation);
    setFormOpen(true);
  };

  // Clean items before sending to API - remove empty item_id fields
  const cleanItemsForAPI = (items: VendorQuotationItem[]) => {
    return items.map((item) => {
      const cleanedItem = { ...item };
      // Remove item_id if it's empty or undefined
      if (!cleanedItem.item_id || cleanedItem.item_id.trim() === "") {
        delete cleanedItem.item_id;
      }
      return cleanedItem;
    });
  };

  const handleSaveQuotation = async () => {
    // Enhanced validations
    if (!formData.supplier_id) {
      toast.error("Please select a supplier");
      return;
    }

    if (formData.quoted_price <= 0) {
      toast.error("Quoted price must be greater than 0");
      return;
    }

    // Validate items pricing
    if (formData.items && formData.items.length > 0) {
      const invalidPriceItems = formData.items.filter(
        (item) => item.price_per_unit < 0
      );
      if (invalidPriceItems.length > 0) {
        toast.error("All item prices must be 0 or greater");
        return;
      }

      const invalidTotalItems = formData.items.filter(
        (item) => item.total_price < 0
      );
      if (invalidTotalItems.length > 0) {
        toast.error("All item totals must be 0 or greater");
        return;
      }
    }

    if (!formData.quote_validity_date) {
      toast.error("Please set a quote validity date");
      return;
    }

    if (formData.quote_validity_date <= new Date()) {
      toast.error("Quote validity date must be in the future");
      return;
    }

    if (formData.estimated_delivery_days < 1) {
      toast.error("Delivery days must be at least 1");
      return;
    }

    setSaving(true);
    try {
      if (editingQuotation) {
        // Update existing quotation
        const updateData: UpdateVendorQuotationRequest = {
          _id: editingQuotation._id,
          quoted_price: formData.quoted_price,
          quoted_price_override: formData.quoted_price_override,
          estimated_delivery_days: formData.estimated_delivery_days,
          quote_validity_date:
            formData.quote_validity_date?.getTime() || Date.now(),
          currency: formData.currency,
          payment_terms: formData.payment_terms,
          delivery_terms: formData.delivery_terms,
          notes: formData.notes,
          items: cleanItemsForAPI(formData.items),
        };

        const response = await rfqService.updateVendorQuotation(updateData);
        if (response.success) {
          toast.success("Quotation updated successfully");
        } else {
          throw new Error(
            response.error?.message || "Failed to update quotation"
          );
        }
      } else {
        // Add new quotation
        const addData: AddVendorQuotationRequest = {
          rfq_id: rfq._id,
          supplier_id: formData.supplier_id,
          quoted_price: formData.quoted_price,
          quoted_price_override: formData.quoted_price_override,
          estimated_delivery_days: formData.estimated_delivery_days,
          quote_validity_date:
            formData.quote_validity_date?.getTime() || Date.now(),
          currency: formData.currency,
          payment_terms: formData.payment_terms,
          delivery_terms: formData.delivery_terms,
          notes: formData.notes,
          items: cleanItemsForAPI(formData.items),
        };

        // Debug: Log the cleaned data being sent
        console.log("Sending quotation data:", addData);
        console.log("Cleaned items:", addData.items);

        const response = await rfqService.addVendorQuotation(addData);
        if (response.success) {
          toast.success("Quotation added successfully");
        } else {
          throw new Error(response.error?.message || "Failed to add quotation");
        }
      }

      setFormOpen(false);
      loadQuotations();
      onUpdate();
    } catch (error: any) {
      console.error("Error saving quotation:", error);
      toast.error(error.message || "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptQuotation = async (quotationId: string) => {
    try {
      const response = await rfqService.acceptVendorQuotation({
        _id: quotationId,
      });
      if (response.success) {
        toast.success("Quotation accepted successfully");
        loadQuotations();
        onUpdate();
      } else {
        throw new Error(
          response.error?.message || "Failed to accept quotation"
        );
      }
    } catch (error: any) {
      console.error("Error accepting quotation:", error);
      toast.error(error.message || "Failed to accept quotation");
    } finally {
      setAnchorEl(null);
      setSelectedQuotationId(null);
    }
  };

  const handleRejectQuotation = async () => {
    if (!quotationToReject || !rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      const response = await rfqService.rejectVendorQuotation({
        _id: quotationToReject,
        rejection_reason: rejectionReason.trim(),
      });
      if (response.success) {
        toast.success("Quotation rejected successfully");
        loadQuotations();
        onUpdate();
        setRejectDialogOpen(false);
        setQuotationToReject(null);
        setRejectionReason("");
      } else {
        throw new Error(
          response.error?.message || "Failed to reject quotation"
        );
      }
    } catch (error: any) {
      console.error("Error rejecting quotation:", error);
      toast.error(error.message || "Failed to reject quotation");
    } finally {
      setAnchorEl(null);
      setSelectedQuotationId(null);
    }
  };

  const openRejectDialog = (quotationId: string) => {
    setQuotationToReject(quotationId);
    setRejectDialogOpen(true);
    setAnchorEl(null);
    setSelectedQuotationId(null);
  };

  const handleDeleteQuotation = async (quotationId: string) => {
    if (!window.confirm("Are you sure you want to delete this quotation?"))
      return;

    try {
      const response = await rfqService.deleteVendorQuotation({
        _id: quotationId,
      });
      if (response.success) {
        toast.success("Quotation deleted successfully");
        loadQuotations();
        onUpdate();
      } else {
        throw new Error(
          response.error?.message || "Failed to delete quotation"
        );
      }
    } catch (error: any) {
      console.error("Error deleting quotation:", error);
      toast.error(error.message || "Failed to delete quotation");
    } finally {
      setAnchorEl(null);
      setSelectedQuotationId(null);
    }
  };

  const availableSuppliers = rfq.supplier_details.filter(
    (supplier) =>
      !quotations.some(
        (q) => q.supplier_id === (supplier.supplier_id || supplier._id)
      )
  );

  // Debug: Log supplier data to help troubleshoot
  console.log("RFQ Supplier Details:", rfq.supplier_details);
  console.log("Available Suppliers:", availableSuppliers);
  console.log("Existing Quotations:", quotations);
  console.log("Current Form Data:", formData);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: "70vh" },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                bgcolor: "primary.main",
                borderRadius: "8px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "white", fontSize: 20 }}>add_business</Icon>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Vendor Quotations
              </Typography>
              <Typography variant="body2" color="textSecondary">
                RFQ: {rfq.rfq_number} • {quotations.length} quotations
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Tabs */}
            <Box sx={{ mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  icon={<Icon>view_list</Icon>}
                  label="List View"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  icon={<Icon>compare_arrows</Icon>}
                  label={`Compare (${quotations.length})`}
                  sx={{ minHeight: 48 }}
                />
              </Tabs>
            </Box>

            {/* Action Buttons - Only show in List View */}
            {activeTab === 0 && (
              <Box sx={{ mb: 3 }}>
                {/* Status Information */}
                {availableSuppliers.length === 0 && quotations.length > 0 && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    icon={<Icon>info</Icon>}
                  >
                    <Typography variant="body2">
                      <strong>
                        All suppliers have been assigned quotations.
                      </strong>{" "}
                      You have received {quotations.length} quotation
                      {quotations.length !== 1 ? "s" : ""} from{" "}
                      {rfq.supplier_details.length} supplier
                      {rfq.supplier_details.length !== 1 ? "s" : ""}. You can
                      edit existing quotations or use the comparison view to
                      evaluate them.
                    </Typography>
                  </Alert>
                )}

                {availableSuppliers.length > 0 && (
                  <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    icon={<Icon>pending_actions</Icon>}
                  >
                    <Typography variant="body2">
                      <strong>
                        {availableSuppliers.length} supplier
                        {availableSuppliers.length !== 1 ? "s" : ""} pending
                        quotation.
                      </strong>{" "}
                      You can add quotations for:{" "}
                      {availableSuppliers
                        .map((s) => s.supplier_name || s.name)
                        .join(", ")}
                      .
                    </Typography>
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Tooltip
                    title={
                      availableSuppliers.length === 0
                        ? "All suppliers have been assigned quotations. You can edit existing quotations instead."
                        : `Add quotation for available suppliers (${availableSuppliers.length} remaining)`
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<Icon>add</Icon>}
                        onClick={handleAddQuotation}
                        disabled={
                          availableSuppliers.length === 0 ||
                          !hasPermission(
                            "inventory.rfq_quotation.create",
                            user?.newPermissions,
                            user?.assignment_details?.id,
                            user?.is_store_owner
                          )
                        }
                      >
                        Add Quotation
                        {availableSuppliers.length > 0 && (
                          <Chip
                            label={availableSuppliers.length}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: "rgba(255,255,255,0.3)",
                              color: "inherit",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      availableSuppliers.length === 0
                        ? "All suppliers have quotations. Import is not available."
                        : "Import quotations from Excel file with supplier selection"
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<Icon>cloud_upload</Icon>}
                        onClick={() => setImportDialogOpen(true)}
                        disabled={
                          availableSuppliers.length === 0 ||
                          !hasPermission(
                            "inventory.rfq_quotation.create",
                            user?.newPermissions,
                            user?.assignment_details?.id,
                            user?.is_store_owner
                          )
                        }
                        sx={{
                          background:
                            "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                          boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                        }}
                      >
                        Import Quotations
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    startIcon={<Icon>refresh</Icon>}
                    onClick={loadQuotations}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            )}

            {/* Content based on active tab */}
            {activeTab === 0 &&
              /* Quotations List */
              (loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography>Loading quotations...</Typography>
                </Box>
              ) : quotations.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50" }}>
                  <Icon sx={{ fontSize: 48, color: "grey.400", mb: 2 }}>
                    add_business
                  </Icon>
                  <Typography variant="h6" gutterBottom>
                    No Quotations Yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Start by adding quotations from your suppliers
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {quotations.map((quotation) => (
                    <Grid item xs={12} md={6} key={quotation._id}>
                      <Card>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6" fontWeight={600}>
                              {quotation.supplier_name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              {getStatusChip(quotation.status)}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setAnchorEl(e.currentTarget);
                                  setSelectedQuotationId(quotation._id);
                                }}
                              >
                                <Icon>more_vert</Icon>
                              </IconButton>
                            </Box>
                          </Box>

                          <Box sx={{ display: "grid", gap: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography variant="body2" color="textSecondary">
                                Price:
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(
                                  quotation.quoted_price,
                                  quotation.currency
                                )}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography variant="body2" color="textSecondary">
                                Delivery:
                              </Typography>
                              <Typography variant="body2">
                                {quotation.estimated_delivery_days} days
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography variant="body2" color="textSecondary">
                                Valid Until:
                              </Typography>
                              <Box sx={{ textAlign: "right" }}>
                                <Typography variant="body2">
                                  {formatDate(quotation.quote_validity_date)}
                                </Typography>
                                {isQuotationExpired(
                                  quotation.quote_validity_date
                                ) && (
                                  <Chip
                                    label="EXPIRED"
                                    size="small"
                                    sx={{
                                      bgcolor: "#FEF2F2",
                                      color: "#DC2626",
                                      fontWeight: 600,
                                      fontSize: 10,
                                      height: 20,
                                      mt: 0.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {quotation.payment_terms && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Payment Terms:
                                </Typography>
                                <Typography variant="body2">
                                  {quotation.payment_terms}
                                </Typography>
                              </Box>
                            )}

                            {quotation.delivery_terms && (
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Delivery Terms:
                                </Typography>
                                <Typography variant="body2">
                                  {quotation.delivery_terms}
                                </Typography>
                              </Box>
                            )}

                            {quotation.notes && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Notes:
                                </Typography>
                                <Typography variant="body2">
                                  {quotation.notes}
                                </Typography>
                              </Box>
                            )}

                            {quotation.rejection_reason && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1,
                                  bgcolor: "error.50",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography variant="body2" color="error.main">
                                  Rejected: {quotation.rejection_reason}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ))}

            {/* Comparison View */}
            {activeTab === 1 && (
              <VendorQuotationComparison
                quotations={quotations}
                onAcceptQuotation={handleAcceptQuotation}
                onRejectQuotation={(quotationId) => {
                  openRejectDialog(quotationId);
                }}
                loading={loading}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>

        {/* Quotation Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedQuotationId(null);
          }}
        >
          {selectedQuotationId &&
            (() => {
              const quotation = quotations.find(
                (q) => q._id === selectedQuotationId
              );
              if (!quotation) return null;

              return [
                quotation.status === "submitted" &&
                  !isQuotationExpired(quotation.quote_validity_date) && (
                    <MenuItem
                      key="accept"
                      onClick={() => handleAcceptQuotation(selectedQuotationId)}
                      sx={{ color: "#059669" }}
                      disabled={
                        !hasPermission(
                          "inventory.rfq_quotation.accept",
                          user?.newPermissions,
                          user?.assignment_details?.id,
                          user?.is_store_owner
                        )
                      }
                    >
                      <Icon sx={{ mr: 1 }}>check_circle</Icon>
                      Accept
                    </MenuItem>
                  ),
                quotation.status === "submitted" &&
                  isQuotationExpired(quotation.quote_validity_date) && (
                    <MenuItem key="expired" disabled sx={{ color: "#DC2626" }}>
                      <Icon sx={{ mr: 1 }}>block</Icon>
                      Cannot Accept (Expired)
                    </MenuItem>
                  ),
                quotation.status === "submitted" && (
                  <MenuItem
                    key="reject"
                    onClick={() => openRejectDialog(selectedQuotationId!)}
                    sx={{ color: "#DC2626" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq_quotation.reject",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 1 }}>cancel</Icon>
                    Reject
                  </MenuItem>
                ),
                (quotation.status === "pending" ||
                  quotation.status === "submitted") && (
                  <MenuItem
                    key="edit"
                    onClick={() => {
                      handleEditQuotation(quotation);
                      setAnchorEl(null);
                      setSelectedQuotationId(null);
                    }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq_quotation.update",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 1 }}>edit</Icon>
                    Edit
                  </MenuItem>
                ),
                quotation.status !== "accepted" && (
                  <MenuItem
                    key="delete"
                    onClick={() => handleDeleteQuotation(selectedQuotationId)}
                    sx={{ color: "#DC2626" }}
                    disabled={
                      !hasPermission(
                        "inventory.rfq_quotation.delete",
                        user?.newPermissions,
                        user?.assignment_details?.id,
                        user?.is_store_owner
                      )
                    }
                  >
                    <Icon sx={{ mr: 1 }}>delete</Icon>
                    Delete
                  </MenuItem>
                ),
              ].filter(Boolean);
            })()}
        </Menu>

        {/* Quotation Form Dialog */}
        <Dialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { minHeight: "70vh" },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: "8px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ color: "white", fontSize: 20 }}>receipt</Icon>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingQuotation ? "Edit Quotation" : "Add New Quotation"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  RFQ: {rfq.rfq_number} • {formData.items?.length || 0} items
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {/* Supplier Selection */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Supplier Information
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Supplier"
                  value={formData.supplier_id || ""}
                  onChange={(e) => {
                    const supplier = rfq.supplier_details.find(
                      (s) => (s.supplier_id || s._id) === e.target.value
                    );
                    setFormData((prev) => ({
                      ...prev,
                      supplier_id: e.target.value,
                      supplier_name:
                        supplier?.supplier_name || supplier?.name || "",
                    }));
                  }}
                  disabled={!!editingQuotation}
                >
                  {(editingQuotation
                    ? [
                        {
                          supplier_id: editingQuotation.supplier_id,
                          supplier_name: editingQuotation.supplier_name,
                          name: editingQuotation.supplier_name,
                          _id: editingQuotation.supplier_id,
                        },
                      ]
                    : availableSuppliers
                  ).map((supplier) => (
                    <MenuItem
                      key={supplier.supplier_id || supplier._id}
                      value={supplier.supplier_id || supplier._id}
                    >
                      {supplier.supplier_name ||
                        supplier.name ||
                        "Unknown Supplier"}
                    </MenuItem>
                  ))}
                </TextField>
              </Paper>

              {/* Items Section */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Item Pricing
                  <Chip
                    label={`${formData.items?.length || 0} items`}
                    size="small"
                    sx={{ ml: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                </Typography>

                {formData.items?.length > 0 ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                        gap: 2,
                        p: 2,
                        bgcolor: "grey.100",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Item Name
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Quantity
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Unit
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Price per Unit
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Total
                      </Typography>
                    </Box>

                    {/* Items */}
                    {formData.items.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr",
                          gap: 2,
                          p: 2,
                          borderTop: index > 0 ? "1px solid" : "none",
                          borderColor: "divider",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {item.item_name}
                          </Typography>
                          {item.urgency && (
                            <Chip
                              label={item.urgency}
                              size="small"
                              variant="outlined"
                              color={
                                item.urgency === "high"
                                  ? "error"
                                  : item.urgency === "medium"
                                  ? "warning"
                                  : "default"
                              }
                              sx={{ fontSize: "0.75rem", mt: 0.5 }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2">{item.quantity}</Typography>
                        <Typography variant="body2">{item.uom}</Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={item.price_per_unit}
                          onChange={(e) =>
                            handleItemPriceChange(index, Number(e.target.value))
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          placeholder="0.00"
                        />
                        <TextField
                          type="number"
                          size="small"
                          value={item.total_price}
                          onChange={(e) =>
                            handleItemTotalChange(index, Number(e.target.value))
                          }
                          inputProps={{ min: 0, step: 0.01 }}
                          placeholder="0.00"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontWeight: 600,
                              color: "primary.main",
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No items available from the purchase request.
                  </Typography>
                )}
              </Paper>

              {/* Quotation Summary */}
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: "primary.50",
                  border: "1px solid",
                  borderColor: "primary.main",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                >
                  Quotation Summary
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Calculated Total:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(
                          formData.calculated_price,
                          formData.currency
                        )}
                      </Typography>
                    </Box>

                    <Box sx={{ position: "relative" }}>
                      <TextField
                        fullWidth
                        label="Quoted Price"
                        type="number"
                        value={formData.quoted_price}
                        onChange={(e) =>
                          handleQuotedPriceChange(Number(e.target.value), true)
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        error={
                          formData.quoted_price_override &&
                          Math.abs(
                            formData.quoted_price - formData.calculated_price
                          ) > 0.01
                        }
                        helperText={
                          formData.quoted_price_override &&
                          Math.abs(
                            formData.quoted_price - formData.calculated_price
                          ) > 0.01
                            ? `Manually overridden (${formatCurrency(
                                formData.calculated_price -
                                  formData.quoted_price,
                                formData.currency
                              )} difference)`
                            : "Auto-calculated from item totals"
                        }
                      />
                      {formData.quoted_price_override &&
                        Math.abs(
                          formData.quoted_price - formData.calculated_price
                        ) > 0.01 && (
                          <Button
                            size="small"
                            onClick={resetToCalculatedPrice}
                            sx={{ mt: 1 }}
                            startIcon={<Icon>refresh</Icon>}
                          >
                            Reset to Calculated
                          </Button>
                        )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Currency"
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          currency: e.target.value,
                        }))
                      }
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      label="Delivery Days"
                      type="number"
                      value={formData.estimated_delivery_days}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          estimated_delivery_days: Number(e.target.value),
                        }))
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Quote Valid Until *"
                      value={formData.quote_validity_date}
                      onChange={(date) =>
                        setFormData((prev) => ({
                          ...prev,
                          quote_validity_date: date,
                        }))
                      }
                      minDate={new Date()}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth required />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Payment Terms"
                      placeholder="e.g., Net 30, COD, Advance payment"
                      value={formData.payment_terms}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          payment_terms: e.target.value,
                        }))
                      }
                      helperText="Optional - Specify payment conditions"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Delivery Terms"
                      placeholder="e.g., FOB, CIF, DDP, Ex-Works"
                      value={formData.delivery_terms}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          delivery_terms: e.target.value,
                        }))
                      }
                      helperText="Optional - Specify delivery conditions"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      multiline
                      rows={3}
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder="Any additional comments or special conditions..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <LoadingButton
              onClick={handleSaveQuotation}
              loading={saving}
              variant="contained"
            >
              {editingQuotation ? "Update" : "Add"} Quotation
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Import Dialog */}
        <EnhancedVendorQuotationImport
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          rfq={rfq}
          onImportComplete={() => {
            loadQuotations();
            onUpdate();
          }}
        />

        {/* Rejection Reason Dialog */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reject Quotation</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Please provide a reason for rejecting this quotation:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason("");
                setQuotationToReject(null);
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleRejectQuotation}
              loading={saving}
              variant="contained"
              color="error"
              disabled={!rejectionReason.trim()}
            >
              Reject Quotation
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Dialog>
    </LocalizationProvider>
  );
};
